import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { cn } from "../lib/cn";

const Root = BasePopover.Root;
const Trigger = BasePopover.Trigger;
const Close = BasePopover.Close;

/** Shared popup surface for Popover, DropdownMenu, and Select. */
export const popupClassName =
  "rounded-lg border border-border bg-surface-raised text-fg shadow-md outline-none origin-(--transform-origin) transition-[opacity,scale] duration-fast ease-enter data-starting-style:scale-[0.97] data-starting-style:opacity-0 data-ending-style:scale-[0.97] data-ending-style:opacity-0 data-instant:transition-none";

export interface PopoverContentProps extends Omit<BasePopover.Popup.Props, "className"> {
  className?: string;
  positionerClassName?: string;
  side?: BasePopover.Positioner.Props["side"];
  align?: BasePopover.Positioner.Props["align"];
  sideOffset?: number;
  alignOffset?: number;
}

function Content({
  className,
  positionerClassName,
  side = "bottom",
  align = "center",
  sideOffset = 6,
  alignOffset = 0,
  ...props
}: PopoverContentProps) {
  const layer = useOverlayLayer("dropdown");
  return (
    <OverlayLayerContext value={layer}>
      <BasePopover.Portal>
        <BasePopover.Positioner
          style={positionerClassName === undefined ? { zIndex: layer } : undefined}
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={cn("outline-none", positionerClassName ?? "z-dropdown")}
        >
          <BasePopover.Popup
            data-slot="popover-content"
            className={cn(popupClassName, "w-72 p-3", className)}
            {...props}
          />
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </OverlayLayerContext>
  );
}

function Title({ className, ...props }: BasePopover.Title.Props & { className?: string }) {
  return (
    <BasePopover.Title
      data-slot="popover-title"
      className={cn("text-sm font-medium text-fg", className)}
      {...props}
    />
  );
}

function Description({
  className,
  ...props
}: BasePopover.Description.Props & { className?: string }) {
  return (
    <BasePopover.Description
      data-slot="popover-description"
      className={cn("text-sm text-fg-muted", className)}
      {...props}
    />
  );
}

export const Popover = { Root, Trigger, Content, Title, Description, Close };
